import { computed, reactive, readonly, ref } from 'vue'

import type { LoaderState, LoadingOperation, LoadingOptions } from '@/types/loader'

// Global state - shared across all instances
const state = reactive<LoaderState>({
  operations: new Map(),
  isLoading: false,
  currentMessage: null,
})

// Accessibility delay state
const shouldShowLoader = ref(false)
const delayTimerId = ref<number | null>(null)

// Default timeout for auto-dismiss (30 seconds)
const DEFAULT_TIMEOUT = 30000
// Accessibility delay to prevent screen flashes (250ms)
const ACCESSIBILITY_DELAY = 250

/**
 * Global loader composable for managing loading states across the application. * Supports multiple concurrent loading operations with auto-dismiss functionality.
 */
export function useGlobalLoader() {
  /**
   * Updates the global loading state based on active operations
   * Implements accessibility delay to prevent screen flashes
   */
  const updateGlobalState = (): void => {
    const operationsArray = Array.from(state.operations.values())
    const hasActiveOperations = operationsArray.length > 0

    state.isLoading = hasActiveOperations

    // Handle accessibility delay for showing loader
    if (hasActiveOperations && !shouldShowLoader.value) {
      // Start the delay timer if not already running
      if (delayTimerId.value === null) {
        delayTimerId.value = window.setTimeout(() => {
          shouldShowLoader.value = true
          delayTimerId.value = null
        }, ACCESSIBILITY_DELAY)
      }
    } else if (!hasActiveOperations) {
      // Clear delay timer and hide loader immediately when no operations
      if (delayTimerId.value !== null) {
        clearTimeout(delayTimerId.value)
        delayTimerId.value = null
      }
      shouldShowLoader.value = false
    }

    // Set current message to the most recent operation with a message
    const operationWithMessage = operationsArray
      .filter((op) => op.message)
      .sort((a, b) => b.startTime - a.startTime)[0]

    state.currentMessage = operationWithMessage?.message || null
  }

  /**
   * Stops a specific loading operation
   * @param key Unique identifier for the operation to stop
   */
  const stopLoading = (key: string): void => {
    const operation = state.operations.get(key)
    if (operation) {
      // Clear the auto-dismiss timer
      clearTimeout(operation.timerId)

      // Remove from operations map
      state.operations.delete(key)

      // Update global state
      updateGlobalState()
    }
  }

  /**
   * Starts a loading operation with optional message and timeout
   * @param key Unique identifier for the operation
   * @param options Configuration options for the loading operation
   */
  const startLoading = (key: string, options: LoadingOptions = {}): void => {
    // Clear existing operation if it exists
    if (state.operations.has(key)) {
      stopLoading(key)
    }

    const timeout = options.timeout || DEFAULT_TIMEOUT

    // Create auto-dismiss timer
    const timerId = window.setTimeout(() => {
      console.warn(`Loading operation '${key}' auto-dismissed after ${timeout}ms`)
      stopLoading(key)
    }, timeout)

    // Create loading operation
    const operation: LoadingOperation = {
      key,
      message: options.message,
      startTime: Date.now(),
      timerId,
    }

    // Add to operations map
    state.operations.set(key, operation)

    // Update global state
    updateGlobalState()
  }

  /**
   * Stops all active loading operations
   */
  const stopAllLoading = (): void => {
    // Clear all timers
    state.operations.forEach((operation) => {
      clearTimeout(operation.timerId)
    })

    // Clear operations map
    state.operations.clear()

    // Update global state
    updateGlobalState()
  }

  /**
   * Gets information about a specific loading operation
   * @param key Unique identifier for the operation
   * @returns Loading operation info or undefined if not found
   */
  const getLoadingInfo = (key: string): LoadingOperation | undefined => {
    return state.operations.get(key)
  }

  /**
   * Checks if a specific operation is currently loading
   * @param key Unique identifier for the operation
   * @returns True if the operation is active
   */
  const isLoadingKey = (key: string): boolean => {
    return state.operations.has(key)
  }

  /**
   * Convenience method for wrapping async operations with loading state
   * @param key Unique identifier for the operation
   * @param asyncFn The async function to execute
   * @param options Configuration options for the loading operation
   * @returns Promise that resolves with the result of the async function
   */
  const withLoading = async <T>(
    key: string,
    asyncFn: () => Promise<T>,
    options: LoadingOptions = {},
  ): Promise<T> => {
    try {
      startLoading(key, options)
      const result = await asyncFn()
      return result
    } finally {
      stopLoading(key)
    }
  }
  // Computed properties for reactive access
  const isLoading = computed(() => state.isLoading)
  const isVisible = computed(() => shouldShowLoader.value)
  const currentMessage = computed(() => state.currentMessage)
  const activeOperationsCount = computed(() => state.operations.size)
  const activeOperations = computed(() => Array.from(state.operations.keys()))

  return {
    // State (readonly)
    isLoading: readonly(isLoading),
    isVisible: readonly(isVisible),
    currentMessage: readonly(currentMessage),
    activeOperationsCount: readonly(activeOperationsCount),
    activeOperations: readonly(activeOperations),

    // Methods
    startLoading,
    stopLoading,
    stopAllLoading,
    getLoadingInfo,
    isLoadingKey,
    withLoading,
  }
}
